import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Sparkles, AlertCircle, CheckCheck, Loader2, AlertTriangle, Link2, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { api, ApiError } from '@/lib/api';
import type { Solicitante, Cota, CotaFamilia, Contrato, SubtipoSetorial, AreaForum, SubtipoBRM, FaseEscopo, EntregaEscopo } from '@/lib/types';
import { FASES_ESCOPO, TEMPLATE_ENTREGAS } from '@/lib/types';

interface Props {
  solicitantes: Solicitante[];
  cotas: Cota[];
  onClose: () => void;
}

const FAMILIAS: { key: CotaFamilia; label: string; cor: string }[] = [
  { key: 'Conecta Folha Business', label: 'Conecta',   cor: 'border-blue/60 text-blue-light bg-blue/10' },
  { key: 'BRM - BUY',             label: 'BRM - BUY', cor: 'border-purple/60 text-purple bg-purple/10' },
  { key: 'Setorial',              label: 'Setorial',  cor: 'border-pink/60 text-pink bg-pink/10' },
  { key: 'Pesquisa',              label: 'Pesquisa',  cor: 'border-green/60 text-green bg-green/10' },
];

/** Famílias que NÃO exigem seleção de cota */
const FAMILIAS_SEM_COTA: CotaFamilia[] = ['Pesquisa', 'Setorial'];

const SUBTIPOS_SETORIAL: { key: SubtipoSetorial; label: string }[] = [
  { key: 'Mundo Business',               label: 'Mundo Business' },
  { key: 'Agro Business',                label: 'Agro Business' },
  { key: 'Data Business Varejo',         label: 'DB Varejo' },
  { key: 'Data Business Health',         label: 'DB Health' },
  { key: 'Data Business Educação',       label: 'DB Educação' },
  { key: 'Data Business Logística',      label: 'DB Logística' },
  { key: 'Data Business Stone Cachoeiro',label: 'DB Stone Cachoeiro' },
  { key: 'Data Business Indústria',      label: 'DB Indústria' },
  { key: 'Data Business Real Estate',    label: 'DB Real Estate' },
  { key: 'Data Business Tech Conference',label: 'DB Tech Conference' },
  { key: 'Fórum Econômico',             label: 'Fórum Econômico' },
];

const AREAS_FORUM: AreaForum[] = ['Research', 'Gestão de Recursos', 'Advisory', 'Investment Banking'];

const SUBTIPOS_BRM: { key: SubtipoBRM; label: string }[] = [
  { key: 'BRM Brasília', label: 'BRM Brasília' },
  { key: 'BRM SP',       label: 'BRM SP' },
  { key: 'BRM NY',       label: 'BRM NY' },
  { key: 'BUY ES',       label: 'BUY ES' },
  { key: 'BUY SP',       label: 'BUY SP' },
  { key: 'BUY SC',       label: 'BUY SC' },
  { key: 'BUY RS',       label: 'BUY RS' },
  { key: 'BUY PR',       label: 'BUY PR' },
  { key: 'BUY PT',       label: 'BUY PT' },
];

function norm(s: string): string {
  return s
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function CadastrarContratoModal({ solicitantes, cotas, onClose }: Props) {
  const qc = useQueryClient();

  // Fluxo: input texto pra nome do cliente; só após escolher família é que mostra cotas
  const [nomeCliente, setNomeCliente] = useState('');
  const [forceCreate, setForceCreate] = useState(false); // 'criar mesmo assim' pra homônimo
  const [linkExistenteId, setLinkExistenteId] = useState<string | null>(null); // 'vincular ao existente'
  const [familia, setFamilia] = useState<CotaFamilia | ''>('');
  const [cotaId, setCotaId] = useState<string>('');
  const [itensSelecionados, setItensSelecionados] = useState<Set<string>>(new Set());
  const [itensQuantidade, setItensQuantidade] = useState<Record<string, number>>({});
  const [subtiposSetorial, setSubtiposSetorial] = useState<Set<SubtipoSetorial>>(new Set());
  const [areaForum, setAreaForum] = useState<AreaForum | ''>('');
  const [subtipoBRM, setSubtipoBRM] = useState<SubtipoBRM | ''>('');
  const [buscaItens, setBuscaItens] = useState('');
  const [mostrarExtras, setMostrarExtras] = useState(false);
  const [demandasAvulsas, setDemandasAvulsas] = useState<string[]>([]);
  const [novaAvulsa, setNovaAvulsa] = useState('');

  // Escopo de entregas por evento (Map: "eventoSubtipo" → Map: "fase-entrega" → { qtd, checked })
  type EscopoItem = { checked: boolean; quantidade: number };
  const [escopo, setEscopo] = useState<Map<string, Map<string, EscopoItem>>>(new Map());
  const [escopoFasesAbertas, setEscopoFasesAbertas] = useState<Set<string>>(new Set());
  const [vigenciaInicio, setVigenciaInicio] = useState(() => new Date().toISOString().slice(0, 10));
  const [vigenciaFim, setVigenciaFim] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ identificador: string; demandas: number } | null>(null);

  const itensQ = useQuery({
    queryKey: ['itens-kit'],
    queryFn: () => api.itensKit.list().then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const itens = itensQ.data ?? [];

  // Checagem em tempo real: nome digitado já existe na carteira?
  const candidato = useMemo(() => {
    const n = norm(nomeCliente);
    if (n.length < 3) return null;
    return solicitantes.find((s) => norm(s.nome) === n) ?? null;
  }, [nomeCliente, solicitantes]);

  // Quando muda a cota, marca todos os itens padrão automaticamente
  useEffect(() => {
    if (!cotaId) { setItensSelecionados(new Set()); setItensQuantidade({}); return; }
    const padrao = itens.filter((i) => i.cotaIds.includes(cotaId)).map((i) => i.id);
    setItensSelecionados(new Set(padrao));
    const qtd: Record<string, number> = {};
    padrao.forEach((id) => { qtd[id] = 1; });
    setItensQuantidade(qtd);
  }, [cotaId, itens]);

  // Quando muda o nome, reseta as flags de homônimo/vincular
  useEffect(() => {
    setForceCreate(false);
    setLinkExistenteId(null);
  }, [nomeCliente]);

  // Popula escopo automaticamente quando subtipos mudam
  useEffect(() => {
    const eventos: string[] = [];
    if (familia === 'Setorial' && subtiposSetorial.size > 0) {
      eventos.push(...Array.from(subtiposSetorial));
    } else if (familia === 'BRM - BUY' && subtipoBRM) {
      eventos.push(subtipoBRM);
    }
    if (eventos.length === 0) { setEscopo(new Map()); return; }

    const next = new Map<string, Map<string, EscopoItem>>();
    for (const ev of eventos) {
      const prev = escopo.get(ev);
      const faseMap = new Map<string, EscopoItem>();
      for (const fase of FASES_ESCOPO) {
        for (const tpl of TEMPLATE_ENTREGAS[fase]) {
          const key = `${fase}::${tpl.entrega}`;
          const existing = prev?.get(key);
          faseMap.set(key, existing ?? { checked: true, quantidade: tpl.qtdPadrao });
        }
      }
      next.set(ev, faseMap);
    }
    setEscopo(next);
    // Abre todas as fases do primeiro evento por padrão
    if (eventos.length > 0) {
      setEscopoFasesAbertas(new Set(eventos.flatMap((ev) => FASES_ESCOPO.map((f) => `${ev}::${f}`))));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familia, subtiposSetorial, subtipoBRM]);

  const cotasDaFamilia = useMemo(() => {
    if (!familia) return [] as Cota[];
    return cotas
      .filter((c) => c.familia === familia)
      .sort((a, b) => {
        const order = { 'Básica': 0, 'Intermediária': 1, 'Premium': 2 } as Record<string, number>;
        return (order[a.hierarquia] ?? 9) - (order[b.hierarquia] ?? 9);
      });
  }, [cotas, familia]);

  const cotaSelecionada = cotas.find((c) => c.id === cotaId);
  const itensDaCota = useMemo(() => itens.filter((i) => i.cotaIds.includes(cotaId)), [itens, cotaId]);

  const createSolicitante = useMutation({
    mutationFn: (nome: string) => api.solicitantes.create({ nome, tipo: 'Cliente externo', status: 'Ativo' }),
  });
  const createContrato = useMutation({
    mutationFn: (body: Partial<Contrato> & { gerarDemandas?: boolean; gerarItens?: string[]; demandasAvulsas?: string[] }) => api.contratos.create(body),
    onSuccess: async (res) => {
      // Gera entregas de escopo se houver
      const entregasParaCriar: Partial<EntregaEscopo>[] = [];
      escopo.forEach((faseMap, eventoSubtipo) => {
        faseMap.forEach((item, key) => {
          if (!item.checked) return;
          const [fase, entrega] = key.split('::');
          entregasParaCriar.push({
            contratoId: res.data.id,
            eventoSubtipo,
            fase: fase as FaseEscopo,
            entrega,
            quantidade: item.quantidade,
            status: 'Pendente',
          });
        });
      });
      if (entregasParaCriar.length > 0) {
        try {
          await api.escopoEntregas.create(entregasParaCriar);
        } catch (e) {
          console.error('Falha ao criar escopo de entregas', e);
        }
      }
      qc.invalidateQueries({ queryKey: ['contratos'] });
      qc.invalidateQueries({ queryKey: ['demandas'] });
      qc.invalidateQueries({ queryKey: ['solicitantes'] });
      qc.invalidateQueries({ queryKey: ['escopo-entregas'] });
      setSuccess({ identificador: res.data.identificador || 'novo contrato', demandas: res.demandasGeradas });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    },
  });

  // Validação do botão Salvar
  const nomeOk = nomeCliente.trim().length >= 3;
  // Bloqueia quando existe candidato E não escolheu nem 'vincular' nem 'criar mesmo assim'
  const precisaDecidir = !!candidato && !forceCreate && linkExistenteId !== candidato.id;
  const familiaSemCota = familia ? FAMILIAS_SEM_COTA.includes(familia as CotaFamilia) : false;
  const podeSubmeter = nomeOk && !!familia && (familiaSemCota || !!cotaId) && !precisaDecidir;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!nomeOk) { setErr('Digite o nome do cliente (mínimo 3 letras)'); return; }
    if (!familia) { setErr('Escolha a família'); return; }
    if (!familiaSemCota && (!cotaId || !cotaSelecionada)) { setErr('Escolha a cota'); return; }
    if (precisaDecidir) {
      setErr('Cliente já existe — escolha vincular ao existente ou criar novo mesmo assim.');
      return;
    }
    setErr(null);

    try {
      let solicitanteId: string;
      let nomeUsado: string;
      if (linkExistenteId) {
        // Reusa solicitante existente
        solicitanteId = linkExistenteId;
        const sol = solicitantes.find((s) => s.id === linkExistenteId);
        nomeUsado = sol?.nome ?? nomeCliente.trim();
      } else {
        // Cria solicitante novo
        const novo = await createSolicitante.mutateAsync(nomeCliente.trim());
        solicitanteId = novo.data.id;
        nomeUsado = novo.data.nome;
      }
      createContrato.mutate({
        solicitanteId,
        ...(cotaId ? { cotaId } : {}),
        ...(familia ? { cotaFamilia: familia as CotaFamilia } : {}),
        ...(subtiposSetorial.size > 0 ? { subtipoSetorial: Array.from(subtiposSetorial) } : {}),
        ...(areaForum ? { areaForum } : {}),
        ...(subtipoBRM ? { subtipoBRM } : {}),
        vigenciaInicio,
        vigenciaFim,
        valorMensal: cotaSelecionada?.valorMensalSugerido ?? 0,
        identificador: `${nomeUsado.replace(/\s+/g, '-')}-${new Date().getFullYear()}`,
        status: 'Vigente',
        gerarDemandas: true,
        gerarItens: Array.from(itensSelecionados),
        gerarItensQuantidade: itensQuantidade,
        demandasAvulsas,
      });
    } catch (e) {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    }
  }

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
        <div onClick={(e) => e.stopPropagation()} className="bg-panel border border-border rounded-2xl w-full max-w-md p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-green/15 flex items-center justify-center mx-auto mb-3">
            <CheckCheck className="w-6 h-6 text-green" />
          </div>
          <h2 className="text-sm font-bold mb-1">Contrato cadastrado</h2>
          <p className="text-xs text-text-2 mb-4">
            <strong>{success.identificador}</strong> salvo. <span className="text-green">{success.demandas} {success.demandas === 1 ? 'demanda gerada' : 'demandas geradas'}</span> em Briefing.
          </p>
          <button type="button" onClick={onClose} className="btn-primary !py-1.5 !px-3 text-xs">
            Fechar
          </button>
        </div>
      </div>
    );
  }

  const submitting = createSolicitante.isPending || createContrato.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-panel border border-border rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
          <div>
            <h2 className="text-sm font-bold">Cadastrar novo contrato</h2>
            <p className="text-[11px] text-text-2 mt-0.5">
              <Sparkles className="w-3 h-3 inline mr-1 text-blue-light" />
              Escolha família e cota — geramos as demandas do kit automaticamente.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4 text-xs">
          {/* CLIENTE */}
          <div>
            <Label>Cliente <span className="text-red">*</span></Label>
            <input
              autoFocus
              type="text"
              value={nomeCliente}
              onChange={(e) => setNomeCliente(e.target.value)}
              placeholder="Nome da empresa (ex.: Cooabriel, BRM Ventures…)"
              className="input w-full text-sm"
            />

            {candidato && !forceCreate && linkExistenteId !== candidato.id && (
              <div className="mt-2 bg-orange/10 border border-orange/40 rounded-md p-2.5 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-orange shrink-0 mt-0.5" />
                  <div className="text-[11px] text-text leading-snug">
                    <strong>{candidato.nome}</strong> já está na carteira.<br />
                    <span className="text-text-2">
                      Esse contrato é um <strong>segundo contrato</strong> pro mesmo cliente (upsell)? Ou é um cliente diferente com nome igual?
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setLinkExistenteId(candidato.id)}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-blue-light bg-blue/15 border border-blue/40 hover:bg-blue/25 rounded transition-colors"
                  >
                    <Link2 className="w-3 h-3" /> Vincular contrato ao existente
                  </button>
                  <button
                    type="button"
                    onClick={() => setForceCreate(true)}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-text-2 bg-panel-2 border border-border hover:text-text hover:bg-panel-2/80 rounded transition-colors"
                  >
                    Criar novo assim mesmo
                  </button>
                </div>
              </div>
            )}

            {linkExistenteId && (
              <div className="mt-2 bg-blue/10 border border-blue/40 rounded-md p-2.5 text-[11px] text-blue-light flex items-center gap-2">
                <Link2 className="w-3 h-3" />
                Contrato será vinculado ao cliente existente <strong className="ml-1">{candidato?.nome}</strong>
                <button type="button" onClick={() => setLinkExistenteId(null)} className="ml-auto text-muted hover:text-text underline text-[10px]">
                  desfazer
                </button>
              </div>
            )}

            {forceCreate && candidato && (
              <div className="mt-2 bg-yellow/10 border border-yellow/30 rounded-md p-2.5 text-[11px] text-yellow flex items-center gap-2">
                <AlertCircle className="w-3 h-3" />
                Cliente homônimo — será criado um <strong>novo registro</strong> separado.
                <button type="button" onClick={() => setForceCreate(false)} className="ml-auto text-muted hover:text-text underline text-[10px]">
                  desfazer
                </button>
              </div>
            )}
          </div>

          {/* FAMÍLIA */}
          <div>
            <Label>Produto <span className="text-red">*</span></Label>
            <div className="grid grid-cols-3 gap-2">
              {FAMILIAS.map((f) => {
                const active = familia === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => { setFamilia(f.key); setCotaId(''); setSubtiposSetorial(new Set()); setAreaForum(''); setSubtipoBRM(''); setBuscaItens(''); }}
                    className={clsx(
                      'border rounded-md py-2.5 px-2 text-center font-semibold transition-colors',
                      active ? f.cor : 'border-border bg-panel-2 text-text-2 hover:text-text hover:border-border-light',
                    )}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SUBTIPO SETORIAL — multi-select */}
          {familia === 'Setorial' && (
            <div>
              <Label>Eventos Setoriais <span className="text-red">*</span> <span className="text-muted normal-case font-normal">(selecione 1 ou mais)</span></Label>
              <div className="grid grid-cols-3 gap-1.5">
                {SUBTIPOS_SETORIAL.map((s) => {
                  const active = subtiposSetorial.has(s.key);
                  return (
                    <button key={s.key} type="button"
                      onClick={() => {
                        const next = new Set(subtiposSetorial);
                        if (active) { next.delete(s.key); } else { next.add(s.key); }
                        setSubtiposSetorial(next);
                        if (!next.has('Fórum Econômico')) setAreaForum('');
                      }}
                      className={clsx(
                        'border rounded-md py-2 px-1.5 text-center text-[11px] font-semibold transition-colors',
                        active ? 'border-pink/60 text-pink bg-pink/10' : 'border-border bg-panel-2 text-text-2 hover:text-text hover:border-border-light',
                      )}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {subtiposSetorial.has('Fórum Econômico') && (
                <div className="mt-3">
                  <Label>Área do Fórum</Label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {AREAS_FORUM.map((a) => {
                      const active = areaForum === a;
                      return (
                        <button key={a} type="button"
                          onClick={() => setAreaForum(a)}
                          className={clsx(
                            'border rounded-md py-2 px-2 text-center text-[11px] font-semibold transition-colors',
                            active ? 'border-pink/60 text-pink bg-pink/10' : 'border-border bg-panel-2 text-text-2 hover:text-text hover:border-border-light',
                          )}
                        >
                          {a}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SUBTIPO BRM */}
          {familia === 'BRM - BUY' && (
            <div>
              <Label>Subtipo BRM - BUY</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {SUBTIPOS_BRM.map((s) => {
                  const active = subtipoBRM === s.key;
                  return (
                    <button key={s.key} type="button"
                      onClick={() => setSubtipoBRM(s.key)}
                      className={clsx(
                        'border rounded-md py-2 px-2 text-center text-[11px] font-semibold transition-colors',
                        active ? 'border-purple/60 text-purple bg-purple/10' : 'border-border bg-panel-2 text-text-2 hover:text-text hover:border-border-light',
                      )}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* COTA — só aparece após escolher família (exceto famílias sem cota) */}
          {familia && !familiaSemCota && (
            <div>
              <Label>Cota contratada <span className="text-red">*</span></Label>
              {cotasDaFamilia.length === 0 ? (
                <div className="bg-panel-2 border border-border rounded-md p-3 text-[11px] text-muted">
                  Nenhuma cota cadastrada pra {familia} ainda. Cadastre primeiro na tabela <em>Cotas</em> do Airtable.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {cotasDaFamilia.map((c) => {
                    const active = cotaId === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setCotaId(c.id)}
                        className={clsx(
                          'border rounded-md p-3 text-left transition-colors',
                          active
                            ? 'border-blue/60 bg-blue/10 ring-1 ring-blue/40'
                            : 'border-border bg-panel-2 hover:border-border-light',
                        )}
                      >
                        <div className={clsx('text-xs font-bold mb-0.5', active ? 'text-blue-light' : 'text-text')}>
                          {c.nome}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted">
                          {c.hierarquia}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {cotaSelecionada && itensDaCota.length > 0 && (
                <div className="mt-3 bg-panel-2/40 border border-border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-text-2">
                      Kit padrão · {itensSelecionados.size}/{itensDaCota.length} selecionados
                    </div>
                    <div className="flex gap-1.5">
                      <button type="button"
                        onClick={() => setItensSelecionados(new Set(itensDaCota.map((i) => i.id)))}
                        className="text-[10px] text-blue-light hover:text-blue">Marcar todos</button>
                      <span className="text-muted">·</span>
                      <button type="button"
                        onClick={() => setItensSelecionados(new Set())}
                        className="text-[10px] text-muted hover:text-text">Limpar</button>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={buscaItens}
                    onChange={(e) => setBuscaItens(e.target.value)}
                    placeholder="Buscar item…"
                    className="input w-full text-xs mb-2"
                  />
                  <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                    {itensDaCota.filter((item) => !buscaItens || norm(item.item).includes(norm(buscaItens))).map((item) => {
                      const checked = itensSelecionados.has(item.id);
                      return (
                        <label key={item.id}
                          className={clsx(
                            'flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors',
                            checked ? 'bg-blue/10' : 'hover:bg-panel-2',
                          )}>
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              const next = new Set(itensSelecionados);
                              if (e.target.checked) next.add(item.id); else next.delete(item.id);
                              setItensSelecionados(next);
                            }}
                            className="mt-0.5 w-3 h-3 accent-blue-light shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[12px] font-semibold text-text truncate">{item.item}</div>
                            {(item.quantidade || item.periodicidade) && (
                              <div className="text-[10px] text-muted">
                                {item.quantidade && <>×{item.quantidade}</>}
                                {item.quantidade && item.periodicidade && ' · '}
                                {item.periodicidade}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Botão pra adicionar extras de OUTRAS cotas */}
                  <button type="button"
                    onClick={() => setMostrarExtras((v) => !v)}
                    className="mt-3 text-[11px] text-blue-light hover:text-blue inline-flex items-center gap-1">
                    {mostrarExtras ? '−' : '+'} Adicionar subprodutos extras (fora do kit)
                  </button>

                  {mostrarExtras && (
                    <div className="mt-2 border-t border-border pt-2 max-h-44 overflow-y-auto space-y-1 pr-1">
                      {itens
                        .filter((i) => !i.cotaIds.includes(cotaId))
                        .filter((item) => !buscaItens || norm(item.item).includes(norm(buscaItens)))
                        .map((item) => {
                          const checked = itensSelecionados.has(item.id);
                          return (
                            <label key={item.id}
                              className={clsx(
                                'flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors',
                                checked ? 'bg-purple/10' : 'hover:bg-panel-2',
                              )}>
                              <input type="checkbox" checked={checked}
                                onChange={(e) => {
                                  const next = new Set(itensSelecionados);
                                  if (e.target.checked) next.add(item.id); else next.delete(item.id);
                                  setItensSelecionados(next);
                                }}
                                className="mt-0.5 w-3 h-3 accent-purple shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-[12px] font-semibold text-text truncate">{item.item}</div>
                                <div className="text-[10px] text-muted">extra · não está no kit padrão</div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  )}

                  {/* Demanda avulsa — input livre */}
                  <div className="mt-3 border-t border-border pt-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-text-2 mb-1.5">
                      Demanda avulsa <span className="text-muted normal-case font-normal">(opcional · fora do escopo padrão)</span>
                    </div>
                    {demandasAvulsas.length > 0 && (
                      <ul className="space-y-1 mb-2">
                        {demandasAvulsas.map((t, idx) => (
                          <li key={idx} className="flex items-center gap-2 px-2 py-1.5 rounded bg-cyan/10 border border-cyan/30">
                            <span className="text-[12px] flex-1">{t}</span>
                            <button type="button"
                              onClick={() => setDemandasAvulsas((arr) => arr.filter((_, i) => i !== idx))}
                              className="text-muted hover:text-red text-[10px]">remover</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        value={novaAvulsa}
                        onChange={(e) => setNovaAvulsa(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && novaAvulsa.trim()) {
                            e.preventDefault();
                            setDemandasAvulsas((arr) => [...arr, novaAvulsa.trim()]);
                            setNovaAvulsa('');
                          }
                        }}
                        placeholder="Ex.: Reels institucional 30s"
                        className="input flex-1 text-xs" />
                      <button type="button"
                        onClick={() => {
                          if (!novaAvulsa.trim()) return;
                          setDemandasAvulsas((arr) => [...arr, novaAvulsa.trim()]);
                          setNovaAvulsa('');
                        }}
                        className="px-3 py-1 text-[11px] font-bold bg-cyan/20 text-cyan border border-cyan/40 rounded hover:bg-cyan/30 transition-colors">
                        + Adicionar
                      </button>
                    </div>
                  </div>

                  <p className="text-[10px] text-muted mt-3 pt-2 border-t border-border">
                    ✨ Vai gerar <strong className="text-text">{itensSelecionados.size + demandasAvulsas.length}</strong> demanda{(itensSelecionados.size + demandasAvulsas.length) === 1 ? '' : 's'} em Briefing automaticamente
                    {demandasAvulsas.length > 0 && <> · <span className="text-cyan">{demandasAvulsas.length} avulsa{demandasAvulsas.length === 1 ? '' : 's'}</span></>}.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ITENS — para famílias sem cota, mostra todos os itens para seleção manual */}
          {familia && familiaSemCota && itens.length > 0 && (
            <div>
              <Label>Itens do contrato</Label>
              <div className="bg-panel-2/40 border border-border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-text-2">
                    Selecione os itens · {itensSelecionados.size} selecionado{itensSelecionados.size !== 1 ? 's' : ''}
                  </div>
                  <div className="flex gap-1.5">
                    <button type="button"
                      onClick={() => setItensSelecionados(new Set(itens.map((i) => i.id)))}
                      className="text-[10px] text-blue-light hover:text-blue">Marcar todos</button>
                    <span className="text-muted">·</span>
                    <button type="button"
                      onClick={() => setItensSelecionados(new Set())}
                      className="text-[10px] text-muted hover:text-text">Limpar</button>
                  </div>
                </div>
                <input
                  type="text"
                  value={buscaItens}
                  onChange={(e) => setBuscaItens(e.target.value)}
                  placeholder="Buscar item…"
                  className="input w-full text-xs mb-2"
                />
                <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                  {itens.filter((item) => !buscaItens || norm(item.item).includes(norm(buscaItens))).map((item) => {
                    const checked = itensSelecionados.has(item.id);
                    return (
                      <label key={item.id}
                        className={clsx(
                          'flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors',
                          checked ? 'bg-green/10' : 'hover:bg-panel-2',
                        )}>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => {
                            const next = new Set(itensSelecionados);
                            if (e.target.checked) next.add(item.id); else next.delete(item.id);
                            setItensSelecionados(next);
                          }}
                          className="mt-0.5 w-3 h-3 accent-green shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] font-semibold text-text truncate">{item.item}</div>
                          {(item.quantidade || item.periodicidade) && (
                            <div className="text-[10px] text-muted">
                              {item.quantidade && <>×{item.quantidade}</>}
                              {item.quantidade && item.periodicidade && ' · '}
                              {item.periodicidade}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Demanda avulsa — input livre */}
                <div className="mt-3 border-t border-border pt-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-text-2 mb-1.5">
                    Demanda avulsa <span className="text-muted normal-case font-normal">(opcional)</span>
                  </div>
                  {demandasAvulsas.length > 0 && (
                    <ul className="space-y-1 mb-2">
                      {demandasAvulsas.map((t, idx) => (
                        <li key={idx} className="flex items-center gap-2 px-2 py-1.5 rounded bg-cyan/10 border border-cyan/30">
                          <span className="text-[12px] flex-1">{t}</span>
                          <button type="button"
                            onClick={() => setDemandasAvulsas((arr) => arr.filter((_, i) => i !== idx))}
                            className="text-muted hover:text-red text-[10px]">remover</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={novaAvulsa}
                      onChange={(e) => setNovaAvulsa(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && novaAvulsa.trim()) {
                          e.preventDefault();
                          setDemandasAvulsas((arr) => [...arr, novaAvulsa.trim()]);
                          setNovaAvulsa('');
                        }
                      }}
                      placeholder="Ex.: Reels institucional 30s"
                      className="input flex-1 text-xs" />
                    <button type="button"
                      onClick={() => {
                        if (!novaAvulsa.trim()) return;
                        setDemandasAvulsas((arr) => [...arr, novaAvulsa.trim()]);
                        setNovaAvulsa('');
                      }}
                      className="px-3 py-1 text-[11px] font-bold bg-cyan/20 text-cyan border border-cyan/40 rounded hover:bg-cyan/30 transition-colors">
                      + Adicionar
                    </button>
                  </div>
                </div>

                <p className="text-[10px] text-muted mt-3 pt-2 border-t border-border">
                  ✨ Vai gerar <strong className="text-text">{itensSelecionados.size + demandasAvulsas.length}</strong> demanda{(itensSelecionados.size + demandasAvulsas.length) === 1 ? '' : 's'} em Briefing automaticamente.
                </p>
              </div>
            </div>
          )}

          {/* ESCOPO DE ENTREGAS — aparece quando há eventos selecionados */}
          {escopo.size > 0 && (
            <div>
              <Label>Escopo de entregas por evento</Label>
              <div className="space-y-3">
                {Array.from(escopo.entries()).map(([evento, faseMap]) => (
                  <div key={evento} className="bg-panel-2/40 border border-border rounded-md p-3">
                    <div className="text-[11px] font-bold text-text mb-2 uppercase tracking-wider">
                      {evento}
                    </div>
                    {FASES_ESCOPO.map((fase) => {
                      const faseKey = `${evento}::${fase}`;
                      const aberta = escopoFasesAbertas.has(faseKey);
                      const itensNaFase = Array.from(faseMap.entries()).filter(([k]) => k.startsWith(`${fase}::`));
                      const checkedCount = itensNaFase.filter(([, v]) => v.checked).length;

                      return (
                        <div key={faseKey} className="mb-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Set(escopoFasesAbertas);
                              if (aberta) next.delete(faseKey); else next.add(faseKey);
                              setEscopoFasesAbertas(next);
                            }}
                            className="flex items-center gap-1.5 w-full text-left py-1 text-[11px] font-semibold text-text-2 hover:text-text transition-colors"
                          >
                            {aberta ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            <span>{fase}</span>
                            <span className="text-muted font-normal ml-auto">{checkedCount}/{itensNaFase.length}</span>
                          </button>

                          {aberta && (
                            <div className="ml-4 space-y-1 mt-1">
                              {itensNaFase.map(([key, item]) => {
                                const entregaNome = key.split('::')[1];
                                return (
                                  <div key={key} className={clsx(
                                    'flex items-center gap-2 px-2 py-1.5 rounded transition-colors',
                                    item.checked ? 'bg-blue/5' : 'opacity-50',
                                  )}>
                                    <input
                                      type="checkbox"
                                      checked={item.checked}
                                      onChange={() => {
                                        const next = new Map(escopo);
                                        const fMap = new Map(next.get(evento)!);
                                        fMap.set(key, { ...item, checked: !item.checked });
                                        next.set(evento, fMap);
                                        setEscopo(next);
                                      }}
                                      className="w-3 h-3 accent-blue-light shrink-0"
                                    />
                                    <span className="text-[11px] flex-1 text-text">{entregaNome}</span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={99}
                                      value={item.quantidade}
                                      onChange={(e) => {
                                        const next = new Map(escopo);
                                        const fMap = new Map(next.get(evento)!);
                                        fMap.set(key, { ...item, quantidade: Math.max(1, parseInt(e.target.value) || 1) });
                                        next.set(evento, fMap);
                                        setEscopo(next);
                                      }}
                                      className="w-12 text-center text-[10px] input !py-0.5 !px-1"
                                      title="Quantidade"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted mt-2">
                ✨ Escopo com <strong className="text-text">
                  {Array.from(escopo.values()).reduce((sum, fm) => sum + Array.from(fm.values()).filter((v) => v.checked).length, 0)}
                </strong> entregas selecionadas em {escopo.size} evento{escopo.size > 1 ? 's' : ''}.
              </p>
            </div>
          )}

          {/* VIGÊNCIA */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vigência início</Label>
              <input type="date" value={vigenciaInicio} onChange={(e) => setVigenciaInicio(e.target.value)} className="input w-full text-sm" />
            </div>
            <div>
              <Label>Vigência fim</Label>
              <input type="date" value={vigenciaFim} onChange={(e) => setVigenciaFim(e.target.value)} className="input w-full text-sm" />
            </div>
          </div>

          {err && (
            <div className="text-xs text-red bg-red/10 border border-red/30 rounded-md px-2.5 py-2 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{err}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-panel">
          <button type="button" onClick={onClose} className="btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
          <button
            type="submit"
            disabled={submitting || !podeSubmeter}
            className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Salvar contrato
          </button>
        </div>
      </form>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-[10px] font-semibold text-text-2 uppercase tracking-wide mb-1.5">{children}</label>;
}
